import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Building2, Send, FileSignature, Users, LogOut, Compass, Bell, PenLine, CheckCircle2 } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { useI18n } from "../i18n/I18nContext";
import LanguageSwitcher from "../components/LanguageSwitcher";
import { activityLabel, ACTIVITIES } from "../data/activities";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from "../components/ui/dropdown-menu";

const STATUSES = ["nuova", "in_lavorazione", "completata", "chiusa"];

function NotesEditor({ request, onSave, t }) {
  const [value, setValue] = useState(request.admin_notes || "");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    await onSave(request.request_id, value);
    setSaving(false);
  };
  return (
    <div className="mt-4 border-t border-sand pt-4">
      <label className="overline text-muted-foreground text-[10px]">{t("admin.notesLabel")}</label>
      <Textarea value={value} onChange={(e) => setValue(e.target.value)}
        data-testid={`admin-notes-input-${request.request_id}`}
        placeholder={t("admin.notesPh")}
        className="mt-1.5 rounded-none border-sand focus-visible:ring-gold min-h-[70px] text-sm" />
      <Button onClick={save} disabled={saving} data-testid={`admin-notes-save-${request.request_id}`}
        className="mt-2 rounded-none bg-midnight text-bone hover:bg-midnight/90 h-9 text-sm transition-all duration-300">
        {saving ? t("admin.saving") : t("admin.saveNote")}
      </Button>
    </div>
  );
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [requests, setRequests] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [notif, setNotif] = useState({ count: 0, items: [] });
  const [filterActivity, setFilterActivity] = useState("all");
  const [byAct, setByAct] = useState([]);

  const loadAll = () => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/admin/companies").then((r) => setCompanies(r.data)).catch(() => {});
    api.get("/admin/requests").then((r) => setRequests(r.data)).catch(() => {});
    api.get("/admin/contracts").then((r) => setContracts(r.data)).catch(() => {});
    api.get("/admin/notifications").then((r) => setNotif(r.data)).catch(() => {});
    api.get("/admin/stats/by-activity").then((r) => setByAct(r.data)).catch(() => {});
  };
  useEffect(() => { loadAll(); }, []);

  const markRead = async () => {
    try { await api.post("/admin/notifications/read"); setNotif({ count: 0, items: [] }); } catch (e) {}
  };

  const countersign = async (id) => {
    try {
      const { data } = await api.post(`/admin/contracts/${id}/countersign`);
      setContracts((prev) => prev.map((c) => (c.contract_id === id ? data : c)));
      toast.success(t("common.countersignToast"));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const typeLabels = { specific_job: t("admin.typeSpecific"), partnership: t("admin.typePartnership") };

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/requests/${id}/status`, { status });
      setRequests((prev) => prev.map((r) => (r.request_id === id ? { ...r, status } : r)));
      toast.success(t("admin.statusUpdated"));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const updateNotes = async (id, admin_notes) => {
    try {
      await api.patch(`/admin/requests/${id}/notes`, { admin_notes });
      setRequests((prev) => prev.map((r) => (r.request_id === id ? { ...r, admin_notes } : r)));
      toast.success(t("admin.noteSaved"));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const doLogout = async () => { await logout(); navigate("/login", { replace: true }); };

  const cards = [
    { label: t("admin.cardCompanies"), value: stats?.users ?? "—", icon: Users },
    { label: t("admin.cardProfiles"), value: stats?.companies ?? "—", icon: Building2 },
    { label: t("admin.cardRequests"), value: stats?.requests ?? "—", icon: Send },
    { label: t("admin.cardContracts"), value: stats?.contracts ?? "—", icon: FileSignature },
  ];

  const fc = filterActivity === "all" ? companies : companies.filter((c) => c.activity_code === filterActivity);
  const fr = filterActivity === "all" ? requests : requests.filter((r) => r.activity_code === filterActivity);
  const expQ = filterActivity === "all" ? "" : `?activity=${filterActivity}`;
  const maxAct = Math.max(1, ...byAct.map((a) => Math.max(a.companies, a.requests)));

  return (
    <div className="min-h-screen mesh-soft" data-testid="admin-dashboard">
      <header className="bg-midnight text-bone">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Compass className="h-5 w-5 text-gold" strokeWidth={1.5} />
            <span className="font-serif text-xl font-bold tracking-tight">Qafila</span>
            <Badge variant="outline" className="rounded-none border-gold/40 text-gold text-xs ms-2">{t("admin.badge")}</Badge>
          </div>
          <div className="flex items-center gap-3">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="relative text-bone/80 hover:text-bone transition-colors" data-testid="notifications-bell">
                  <Bell className="h-5 w-5" strokeWidth={1.5} />
                  {notif.count > 0 && (
                    <span className="absolute -top-2 -end-2 bg-gold text-midnight text-[10px] font-bold rounded-full min-w-[16px] h-4 px-1 flex items-center justify-center" data-testid="notifications-count">
                      {notif.count}
                    </span>
                  )}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="rounded-none bg-white border-sand w-80 p-0">
                <div className="flex items-center justify-between px-4 py-3 border-b border-sand">
                  <span className="font-serif text-midnight">{t("common.notifications")}</span>
                  {notif.count > 0 && (
                    <button onClick={markRead} data-testid="notifications-mark-read" className="text-xs text-gold hover:underline">{t("common.markAllRead")}</button>
                  )}
                </div>
                <div className="max-h-80 overflow-y-auto" data-testid="notifications-list">
                  {notif.items.length === 0 ? (
                    <p className="px-4 py-6 text-sm text-muted-foreground text-center">{t("common.noNotifications")}</p>
                  ) : notif.items.map((r) => (
                    <div key={r.request_id} className="px-4 py-3 border-b border-sand/60">
                      <p className="text-sm text-midnight font-medium">{r.title}</p>
                      <p className="text-xs text-muted-foreground">{r.company_name}</p>
                    </div>
                  ))}
                </div>
              </DropdownMenuContent>
            </DropdownMenu>
            <LanguageSwitcher variant="dark" />
            <span className="text-sm text-bone/70 hidden sm:inline">{user?.email}</span>
            <button onClick={doLogout} data-testid="admin-logout-btn" className="flex items-center gap-2 text-sm text-bone/70 hover:text-bone transition-colors">
              <LogOut className="h-4 w-4 rtl:rotate-180" strokeWidth={1.5} /> {t("admin.logout")}
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-10">
        <p className="overline text-gold">{t("admin.panelOverline")}</p>
        <h1 className="font-serif text-3xl sm:text-4xl font-bold text-midnight mt-2 tracking-tight">{t("admin.title")}</h1>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mt-8">
          {cards.map((c, i) => (
            <div key={i} className="relative overflow-hidden border border-sand bg-white p-6 hover:-translate-y-1 transition-all duration-300 group" style={{ borderTop: `3px solid ${["#D4AF37", "#2A9D8F", "#E76F51", "#457B9D"][i]}` }} data-testid={`admin-stat-${i}`}>
              <div className="absolute -top-8 -right-8 w-20 h-20 rounded-full blur-2xl opacity-20 group-hover:opacity-45 transition-opacity" style={{ background: ["#D4AF37", "#2A9D8F", "#E76F51", "#457B9D"][i] }} />
              <c.icon className="relative h-6 w-6" strokeWidth={1.5} style={{ color: ["#D4AF37", "#2A9D8F", "#E76F51", "#457B9D"][i] }} />
              <p className="text-sm text-muted-foreground mt-4">{c.label}</p>
              <p className="font-serif text-3xl text-midnight mt-1">{c.value}</p>
            </div>
          ))}
        </div>

        <Tabs defaultValue="requests" className="mt-10">
          <div className="flex items-center gap-3 mb-4">
            <span className="text-sm text-muted-foreground">{t("common.filterActivity")}:</span>
            <Select value={filterActivity} onValueChange={setFilterActivity}>
              <SelectTrigger className="w-64 rounded-none border-sand" data-testid="portfolio-filter-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="rounded-none bg-white max-h-80">
                <SelectItem value="all">{t("common.allActivities")}</SelectItem>
                {ACTIVITIES.map((a) => (
                  <SelectItem key={a.code} value={a.code}>{a.code} · {activityLabel(a.code, lang)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <TabsList className="rounded-none bg-transparent border-b border-sand p-0 h-auto w-full justify-start gap-6">
            <TabsTrigger value="requests" data-testid="tab-requests" className="rounded-none border-b-2 border-transparent data-[state=active]:border-gold data-[state=active]:bg-transparent data-[state=active]:text-midnight px-1 pb-3 text-muted-foreground">{t("admin.tabRequests")}</TabsTrigger>
            <TabsTrigger value="companies" data-testid="tab-companies" className="rounded-none border-b-2 border-transparent data-[state=active]:border-gold data-[state=active]:bg-transparent data-[state=active]:text-midnight px-1 pb-3 text-muted-foreground">{t("admin.tabCompanies")}</TabsTrigger>
            <TabsTrigger value="contracts" data-testid="tab-contracts" className="rounded-none border-b-2 border-transparent data-[state=active]:border-gold data-[state=active]:bg-transparent data-[state=active]:text-midnight px-1 pb-3 text-muted-foreground">{t("admin.tabContracts")}</TabsTrigger>
            <TabsTrigger value="stats" data-testid="tab-stats" className="rounded-none border-b-2 border-transparent data-[state=active]:border-gold data-[state=active]:bg-transparent data-[state=active]:text-midnight px-1 pb-3 text-muted-foreground">{t("common.statistics")}</TabsTrigger>
          </TabsList>

          <TabsContent value="requests" className="mt-6">
            <div className="flex justify-end mb-3">
              <a href={`${BACKEND}/api/admin/export/requests${expQ}`} download data-testid="export-requests-btn">
                <Button variant="outline" className="rounded-none border-midnight text-midnight hover:bg-midnight hover:text-bone h-8 text-xs transition-all duration-300">{t("common.exportExcel")}</Button>
              </a>
            </div>
            <div className="border border-sand bg-white divide-y divide-sand" data-testid="admin-requests-list">
              {fr.length === 0 && <p className="p-8 text-muted-foreground text-sm">{t("admin.noRequests")}</p>}
              {Object.entries(fr.reduce((acc, r) => { const k = r.activity_code || "_"; (acc[k] = acc[k] || []).push(r); return acc; }, {})).map(([gcode, glist]) => (
                <div key={gcode} data-testid={`request-group-${gcode}`}>
                  <div className="px-6 py-2 bg-midnight/[0.04] text-xs font-medium text-midnight border-b border-sand">
                    {gcode !== "_" ? `${gcode} · ${activityLabel(gcode, lang)}` : t("common.activityNone")} ({glist.length})
                  </div>
                  {glist.map((r) => (
                <div key={r.request_id} className="p-6" data-testid={`admin-request-${r.request_id}`}>
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge variant="outline" className="rounded-none border-sand text-xs">{typeLabels[r.type]}</Badge>
                        {r.activity_code && <Badge variant="outline" className="rounded-none border-gold/40 bg-gold/10 text-midnight text-xs">{r.activity_code} · {activityLabel(r.activity_code, lang)}</Badge>}
                        <span className="text-xs text-muted-foreground">{r.company_name}</span>
                      </div>
                      <h3 className="font-serif text-lg text-midnight mt-2">{r.title}</h3>
                      <p className="text-sm text-muted-foreground mt-1">{r.description}</p>
                      {r.timeline && <p className="text-xs text-muted-foreground mt-1">{t("admin.timelinePrefix")} {r.timeline}</p>}
                    </div>
                    <Select value={r.status} onValueChange={(v) => updateStatus(r.request_id, v)}>
                      <SelectTrigger className="w-44 rounded-none border-sand" data-testid={`admin-status-select-${r.request_id}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="rounded-none bg-white">
                        {STATUSES.map((s) => (
                          <SelectItem key={s} value={s}>{t(`requests.status.${s}`)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <NotesEditor request={r} onSave={updateNotes} t={t} />
                </div>
                  ))}
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="companies" className="mt-6">
            <div className="flex items-center justify-between mb-3">
              <p className="overline text-muted-foreground">{t("common.byActivity")}</p>
              <a href={`${BACKEND}/api/admin/export/companies${expQ}`} download data-testid="export-companies-btn">
                <Button variant="outline" className="rounded-none border-midnight text-midnight hover:bg-midnight hover:text-bone h-8 text-xs transition-all duration-300">{t("common.exportExcel")}</Button>
              </a>
            </div>
            <div className="border border-sand bg-white divide-y divide-sand" data-testid="admin-companies-list">
              {fc.length === 0 && <p className="p-8 text-muted-foreground text-sm">{t("admin.noCompanies")}</p>}
              {Object.entries(fc.reduce((acc, c) => { const k = c.activity_code || "_"; (acc[k] = acc[k] || []).push(c); return acc; }, {})).map(([code, list]) => (
                <div key={code} data-testid={`company-group-${code}`}>
                  <div className="px-6 py-2 bg-midnight/[0.04] text-xs font-medium text-midnight border-b border-sand">
                    {code !== "_" ? `${code} · ${activityLabel(code, lang)}` : t("common.activityNone")} ({list.length})
                  </div>
                  {list.map((c) => (
                    <div key={c.company_id} className="p-6 border-b border-sand/60" data-testid={`admin-company-${c.company_id}`}>
                      <div className="flex flex-wrap items-baseline justify-between gap-2">
                        <h3 className="font-serif text-lg text-midnight">{c.legal_name}</h3>
                        <span className="text-xs text-muted-foreground">{c.email}</span>
                      </div>
                      <div className="mt-2 grid sm:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-1 text-sm text-muted-foreground">
                        <span>{t("admin.sector")}: <span className="text-midnight">{c.industry || "—"}</span></span>
                        <span>{t("admin.site")}: <span className="text-midnight">{c.website || "—"}</span></span>
                        <span>{t("admin.referent")}: <span className="text-midnight">{c.contact_person_name || "—"}</span></span>
                        <span>{t("admin.tel")}: <span className="text-midnight">{c.contact_phone || "—"}</span></span>
                      </div>
                      {c.description && <p className="text-xs text-muted-foreground mt-2 line-clamp-2">{c.description}</p>}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="contracts" className="mt-6">
            <div className="border border-sand bg-white divide-y divide-sand" data-testid="admin-contracts-list">
              {contracts.length === 0 && <p className="p-8 text-muted-foreground text-sm">{t("admin.noContracts")}</p>}
              {contracts.map((c) => (
                <div key={c.contract_id} className="p-6 flex flex-wrap items-center justify-between gap-3" data-testid={`admin-contract-${c.contract_id}`}>
                  <div>
                    <span className="font-serif text-lg text-midnight">{c.contract_number}</span>
                    <p className="text-sm text-muted-foreground">{c.company_name} · {c.start_date} → {c.end_date} · {c.duration_years || 1}y</p>
                    {c.countersigned && (
                      <p className="text-xs text-green-700 mt-1" data-testid={`contract-countersigned-${c.contract_id}`}>
                        {t("common.countersignedOn", { date: c.countersigned_at_display })}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge variant="outline" className="rounded-none border-gold/40 bg-gold/10 text-midnight text-xs">{t("admin.fee")} {c.success_fee_pct}%</Badge>
                    {c.countersigned ? (
                      <Badge variant="outline" className="rounded-none border-green-500/40 bg-green-50 text-green-700 text-xs flex items-center gap-1" data-testid={`countersigned-badge-${c.contract_id}`}>
                        <CheckCircle2 className="h-3 w-3" strokeWidth={2} /> {t("common.countersignedBadge")}
                      </Badge>
                    ) : c.signed ? (
                      <Button onClick={() => countersign(c.contract_id)} data-testid={`countersign-btn-${c.contract_id}`}
                        className="rounded-none bg-midnight text-bone hover:bg-midnight/90 h-9 text-sm transition-all duration-300">
                        <PenLine className="h-4 w-4 me-2" strokeWidth={1.5} /> {t("common.countersign")}
                      </Button>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="stats" className="mt-6">
            <div className="border border-sand bg-white p-6" data-testid="admin-stats-by-activity">
              <div className="flex items-center gap-4 mb-5 text-xs text-muted-foreground">
                <span className="flex items-center gap-1"><span className="w-3 h-3 bg-gold inline-block" />{t("common.statsCompanies")}</span>
                <span className="flex items-center gap-1"><span className="w-3 h-3 bg-midnight inline-block" />{t("common.statsRequests")}</span>
              </div>
              {byAct.length === 0 ? (
                <p className="text-sm text-muted-foreground">—</p>
              ) : byAct.map((a) => (
                <div key={a.code} className="py-2.5" data-testid={`stat-bar-${a.code}`}>
                  <div className="flex justify-between text-xs text-midnight mb-1">
                    <span>{a.code} · {activityLabel(a.code, lang)}</span>
                    <span className="text-muted-foreground">{a.companies} / {a.requests}</span>
                  </div>
                  <div className="space-y-1">
                    <div className="h-2 bg-gold transition-all" style={{ width: `${(a.companies / maxAct) * 100}%` }} />
                    <div className="h-2 bg-midnight transition-all" style={{ width: `${(a.requests / maxAct) * 100}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
